import { useState } from 'react';
import { Button, Badge } from './ui';
import { useSubmitFeedback, useFeedback, useReviewVersions, useRollbackReview } from '../hooks/useApi';
import { RecommendationBadge } from './StatusBadge';
import type { FeedbackType, ReviewVersion, FeedbackEntry } from '../types';
import {
  MessageSquare,
  Send,
  ChevronDown,
  ChevronUp,
  History,
  RotateCcw,
  CheckCircle,
  Clock,
  AlertTriangle,
} from 'lucide-react';

interface FeedbackPanelProps {
  applicationId: number;
  /** When true, the submit form is forced open (e.g. from "Request Revision" button) */
  forceShowForm?: boolean;
  /** Called when the form is toggled open/closed */
  onFormToggle?: (open: boolean) => void;
}

const FEEDBACK_TYPES: { value: FeedbackType; label: string }[] = [
  { value: 'interview_notes', label: 'Interview Notes' },
  { value: 'screening_notes', label: 'Screening Notes' },
  { value: 'transcript', label: 'Transcript' },
  { value: 'criticism', label: 'Review Criticism' },
];

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getScoreColor = (rawScore: number): string => {
  const score100 = rawScore * 2;
  if (score100 >= 80) return 'text-green-600';
  if (score100 >= 60) return 'text-blue-600';
  if (score100 >= 40) return 'text-amber-600';
  return 'text-red-600';
};

const getScoreBgColor = (rawScore: number): string => {
  const score100 = rawScore * 2;
  if (score100 >= 80) return 'bg-green-50';
  if (score100 >= 60) return 'bg-blue-50';
  if (score100 >= 40) return 'bg-amber-50';
  return 'bg-red-50';
};

export function FeedbackPanel({ applicationId, forceShowForm, onFormToggle }: FeedbackPanelProps) {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('criticism');
  const [content, setContent] = useState('');
  const [showForm, setShowFormInternal] = useState(false);

  // Allow parent to force the form open
  const isFormOpen = forceShowForm || showForm;
  const setShowForm = (open: boolean) => {
    setShowFormInternal(open);
    onFormToggle?.(open);
  };
  const [showHistory, setShowHistory] = useState(false);
  const [showVersions, setShowVersions] = useState(true);
  const [rollbackTarget, setRollbackTarget] = useState<number | null>(null);

  const submitMutation = useSubmitFeedback();
  const { data: feedbackData, isLoading: feedbackLoading } = useFeedback(applicationId);
  const { data: versionsData, isLoading: versionsLoading } = useReviewVersions(applicationId);
  const rollbackMutation = useRollbackReview();

  const handleSubmit = async () => {
    if (!content.trim()) return;
    try {
      await submitMutation.mutateAsync({
        applicationId,
        feedbackType,
        content: content.trim(),
      });
      setContent('');
      setShowForm(false);
      onFormToggle?.(false);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  const handleRollback = async (reviewId: number) => {
    try {
      await rollbackMutation.mutateAsync({ applicationId, reviewId });
      setRollbackTarget(null);
    } catch (err) {
      console.error('Failed to rollback:', err);
    }
  };

  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());

  const toggleNote = (id: number) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const feedbackEntries = feedbackData?.data ?? [];
  const versions = versionsData?.allVersions ?? [];
  return (
    <div className="space-y-4">
      {/* Review Versions */}
      {versions.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => setShowVersions(!showVersions)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">
                Review Versions ({versions.length})
              </h3>
            </div>
            {showVersions ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {showVersions && (
            <div className="border-t border-gray-100">
              {versionsLoading ? (
                <div className="p-4 text-center text-sm text-gray-500">Loading versions...</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {versions.map((version: ReviewVersion) => {
                    const isActive = version.archivedAt === null;
                    return (
                      <div
                        key={version.id}
                        className={`p-4 ${isActive ? 'bg-primary-50/30' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                v{version.version}
                              </span>
                              {isActive ? (
                                <Badge variant="success" size="xs">Active</Badge>
                              ) : (
                                <Badge variant="neutral" size="xs">Archived</Badge>
                              )}
                              <RecommendationBadge level={version.recommendation} size="sm" />
                            </div>

                            <div className="flex items-center gap-3 text-xs text-gray-500">
                              <span className={`font-medium ${getScoreColor(version.overallScore)}`}>
                                {(version.overallScore / 10).toFixed(1)}/5.0
                              </span>
                              {version.modelUsed && (
                                <span>{version.modelUsed.split('/').pop()}</span>
                              )}
                              <span>{formatDate(version.createdAt)}</span>
                            </div>

                            {version.reprocessReason && (
                              <p className="mt-1 text-xs text-gray-500">
                                Reason: {version.reprocessReason.replace(/_/g, ' ')}
                              </p>
                            )}

                            {version.summary && (
                              <p className="mt-1 text-xs text-gray-600 line-clamp-2">
                                {version.summary}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            {/* Score circle */}
                            <div className={`h-9 w-9 rounded-full flex items-center justify-center ${getScoreBgColor(version.overallScore)}`}>
                              <span className={`text-xs font-bold ${getScoreColor(version.overallScore)}`}>
                                {(version.overallScore / 10).toFixed(1)}
                              </span>
                            </div>

                            {/* Rollback button for archived reviews */}
                            {!isActive && (
                              <>
                                {rollbackTarget === version.id ? (
                                  <div className="flex items-center gap-1 ml-2">
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleRollback(version.id)}
                                      loading={rollbackMutation.isPending}
                                    >
                                      Confirm
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setRollbackTarget(null)}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setRollbackTarget(version.id)}
                                    title="Restore this version"
                                    className="ml-2"
                                  >
                                    <RotateCcw className="h-3 w-3" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Feedback Submission */}
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => setShowForm(!isFormOpen)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900">Submit Feedback & Request Revision</h3>
          </div>
          {isFormOpen ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </button>

        {isFormOpen && (
          <div className="border-t border-gray-100 p-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Feedback Type
              </label>
              <select
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              >
                {FEEDBACK_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Content
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter interview notes, screening observations, or other feedback..."
                rows={4}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-y"
              />
            </div>

            {submitMutation.isError && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4" />
                Failed to submit: {(submitMutation.error as Error).message}
              </div>
            )}

            {submitMutation.isSuccess && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" />
                Feedback submitted. Review is being reprocessed.
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">
                PII will be automatically stripped from condensed version
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSubmit}
                disabled={!content.trim()}
                loading={submitMutation.isPending}
                leftIcon={<Send className="h-3 w-3" />}
              >
                Submit & Reprocess
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Feedback History */}
      {feedbackEntries.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">
                Feedback History ({feedbackEntries.length})
              </h3>
            </div>
            {showHistory ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {showHistory && (
            <div className="border-t border-gray-100 divide-y divide-gray-100">
              {feedbackLoading ? (
                <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
              ) : (
                feedbackEntries.map((entry: FeedbackEntry) => {
                  const isExpanded = expandedNotes.has(entry.id);
                  const content = entry.condensedContent;
                  const isLong = content.length > 120;
                  return (
                    <div key={entry.id} className="p-4">
                      <button
                        onClick={() => toggleNote(entry.id)}
                        className="w-full text-left"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="info" size="xs">
                            {entry.feedbackType.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {formatDate(entry.submittedAt)}
                          </span>
                          {entry.condensedTokens && (
                            <span className="text-xs text-gray-400">
                              {entry.condensedTokens} tokens
                            </span>
                          )}
                          {isLong && (
                            isExpanded
                              ? <ChevronUp className="h-3 w-3 text-gray-400 ml-auto" />
                              : <ChevronDown className="h-3 w-3 text-gray-400 ml-auto" />
                          )}
                        </div>
                        <p className={`text-sm text-gray-600 whitespace-pre-wrap ${!isExpanded && isLong ? 'line-clamp-2' : ''}`}>
                          {content}
                        </p>
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

FeedbackPanel.displayName = 'FeedbackPanel';
